/** Blocking-zone helpers: surface connectivity from indexed mesh, merge draft paint into stored zones. */

export const BLOCKING_PAINT_THRESH = 0.01;

function newZoneId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `bz-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/**
 * Undirected adjacency from triangle list.
 * @param { { array: ArrayLike<number> } | null } indexAttr
 * @param {number} vertexCount
 * @returns {number[][]}
 */
export function buildVertexAdjacency(indexAttr, vertexCount) {
  const adj = Array.from({ length: vertexCount }, () => []);
  const addEdge = (a, b) => {
    if (a === b || a < 0 || b < 0 || a >= vertexCount || b >= vertexCount) return;
    adj[a].push(b);
    adj[b].push(a);
  };

  if (indexAttr) {
    const arr = indexAttr.array;
    for (let i = 0; i < arr.length; i += 3) {
      addEdge(arr[i], arr[i + 1]);
      addEdge(arr[i + 1], arr[i + 2]);
      addEdge(arr[i + 2], arr[i]);
    }
  } else {
    for (let i = 0; i + 2 < vertexCount; i += 3) {
      addEdge(i, i + 1);
      addEdge(i + 1, i + 2);
      addEdge(i + 2, i);
    }
  }
  return adj;
}

/**
 * @param {number} vertexCount
 * @param {number[][]} adj
 * @param {(i: number) => boolean} isActive
 * @returns {{ labels: Int32Array, compCount: number }}
 */
export function labelPaintedComponents(vertexCount, adj, isActive) {
  const labels = new Int32Array(vertexCount).fill(-1);
  let compCount = 0;
  for (let s = 0; s < vertexCount; s++) {
    if (!isActive(s) || labels[s] !== -1) continue;
    const stack = [s];
    labels[s] = compCount;
    while (stack.length) {
      const v = stack.pop();
      for (const w of adj[v]) {
        if (!isActive(w) || labels[w] !== -1) continue;
        labels[w] = compCount;
        stack.push(w);
      }
    }
    compCount++;
  }
  return { labels, compCount };
}

function splitMaskByComponents(draft, labels, compCount, vertexCount) {
  const masks = Array.from({ length: compCount }, () => new Float32Array(vertexCount));
  for (let i = 0; i < vertexCount; i++) {
    const lab = labels[i];
    if (lab < 0 || draft[i] <= BLOCKING_PAINT_THRESH) continue;
    masks[lab][i] = draft[i];
  }
  return masks.filter((m) => {
    for (let i = 0; i < vertexCount; i++) if (m[i] > BLOCKING_PAINT_THRESH) return true;
    return false;
  });
}

function masksTouch(maskA, maskB, adj, vertexCount) {
  for (let i = 0; i < vertexCount; i++) {
    if (maskA[i] <= BLOCKING_PAINT_THRESH) continue;
    if (maskB[i] > BLOCKING_PAINT_THRESH) return true;
    for (const w of adj[i]) {
      if (maskB[w] > BLOCKING_PAINT_THRESH) return true;
    }
  }
  return false;
}

function mergeMasks(masks, vertexCount) {
  const out = new Float32Array(vertexCount);
  for (const m of masks) {
    if (!m || m.length !== vertexCount) continue;
    for (let i = 0; i < vertexCount; i++) {
      out[i] = Math.max(out[i], m[i]);
    }
  }
  return out;
}

/**
 * Split draft into surface-connected pieces, merge into existing zones when adjacency/overlap touches.
 * @param {Float32Array} draft
 * @param {{ id: string, mask: Float32Array }[]} appliedZones
 * @param {number[][]} adj
 * @param {number} vertexCount
 */
export function mergeDraftIntoAppliedZones(draft, appliedZones, adj, vertexCount) {
  if (!draft || draft.length !== vertexCount) return appliedZones;

  const isActive = (i) => draft[i] > BLOCKING_PAINT_THRESH;
  let any = false;
  for (let i = 0; i < vertexCount; i++) {
    if (isActive(i)) {
      any = true;
      break;
    }
  }
  if (!any) return appliedZones;

  const { labels, compCount } = labelPaintedComponents(vertexCount, adj, isActive);
  const draftPieces = splitMaskByComponents(draft, labels, compCount, vertexCount);
  if (draftPieces.length === 0) return appliedZones;

  let zones = appliedZones.map((z) => ({
    id: z.id,
    mask: Float32Array.from(z.mask),
  }));

  for (const piece of draftPieces) {
    const mergeIdx = [];
    for (let zi = 0; zi < zones.length; zi++) {
      if (masksTouch(piece, zones[zi].mask, adj, vertexCount)) mergeIdx.push(zi);
    }
    if (mergeIdx.length === 0) {
      zones.push({ id: newZoneId(), mask: piece });
    } else {
      const toMerge = mergeIdx.map((i) => zones[i].mask);
      toMerge.push(piece);
      const merged = mergeMasks(toMerge, vertexCount);
      mergeIdx.sort((a, b) => b - a);
      for (const idx of mergeIdx) zones.splice(idx, 1);
      zones.push({ id: newZoneId(), mask: merged });
    }
  }
  return zones;
}

export function countMaskedVertices(mask, vertexCount, thresh = BLOCKING_PAINT_THRESH) {
  let n = 0;
  const lim = Math.min(vertexCount, mask?.length ?? 0);
  for (let i = 0; i < lim; i++) if (mask[i] > thresh) n++;
  return n;
}
